/** @jsx jsx */
import React, { useEffect, useState } from 'react';
import styled, { Colors, Fonts } from '../../../theme';
import { FieldProps } from 'formik';
import Cleave from 'cleave.js/react';
import '../../../styles/fonts.css';
import { CleaveOptions } from 'cleave.js/options';
import { css, jsx } from '@emotion/react';

const TextInputWrapper = styled('div')<{
  validationError: boolean;
  autoFocus?: boolean;
  focusCount: number;
  disabled: boolean;
}>`
  color: ${Colors.white};
  border-radius: 5px;
  width: 100%;
  transition: all 0.3s ease-in-out;
  position: relative;
  background-color: ${(props) => (props.disabled ? Colors.lightGray : 'default')};
  & input {
    padding: 0.5rem 0.75rem 0 0.75rem;
    border: 0.16rem solid
      ${(props) =>
        props.validationError && (props.autoFocus ? props.focusCount > 1 : true)
          ? Colors.darkStrawberry
          : Colors.lightGray};
    border-radius: 5px;
    color: ${Colors.richBlack};
    height: 3.4rem;
    width: 100%;
    font-size: 1.375rem;
    font-family: ${Fonts.interR}, ${Fonts.montserrat}, sans-serif;

    :focus {
      border: 0.16rem ${Colors.indigo} solid;
    }

    :focus + label,
    :valid + label,
    :disabled + label {
      font-size: 0.625rem;
      line-height: 0.875rem;
      color: ${Colors.coolGray};
      text-transform: uppercase;
      font-family: ${Fonts.interR}, ${Fonts.montserrat}, sans-serif;
      top: 0.1rem;
      left: 0.2rem;
      padding: 0 0 0 0.61rem;
    }

    :required {
      box-shadow: none;
    }
  }

  @media (max-width: 420px) {
    :after {
      content: unset !important;
    }
  }
`;

const StyledIcon = styled('i')<{
  iconName: IconType;
  color: string;
}>`
  font-family: ${Fonts.fontello}, sans-serif;
  font-size: 1rem;
  position: absolute;
  top: 0.9rem;
  color: ${(props) => props.color};
  :before {
    content: ${(props) => props.iconName};
  }
`;

export enum IconType {
  Helper = '"\\E86A"',
  Error = '"\\E802"',
  Valid = '"\\E827"',
}

const ValidationError = styled('span')`
  display: inline-block;
  width: 100%;
  font-size: 0.75rem;
  line-height: 1.125rem;
  text-align: start;
  margin-top: 0.313rem;
  text-transform: uppercase;
  font-family: ${Fonts.interSB}, ${Fonts.montserrat}, sans-serif;
  color: ${Colors.darkStrawberry};
`;

const Label = styled('label')<{}>`
  font-size: 1.25rem;
  font-weight: 400;
  color: ${Colors.gray};
  left: 0.65rem;
  position: absolute;
  pointer-events: none;
  top: 1.4rem;
  transition: all 0.4s ease;
`;

const focusCssStyle = css`
  font-size: 0.625rem;
  line-height: 0.875rem;
  color: ${Colors.coolGray};
  text-transform: uppercase;
  font-family: ${Fonts.interR}, ${Fonts.montserrat}, sans-serif;
  top: 0.1rem;
  left: 0.2rem;
  padding: 0 0 0 0.61rem;
`;

type AutocompleteType =
  | 'on'
  | 'off'
  | 'name'
  | 'honorific-prefix'
  | 'given-name'
  | 'additional-name'
  | 'family-name'
  | 'honorific-suffix'
  | 'nickname'
  | 'email'
  | 'username'
  | 'new-password'
  | 'current-password'
  | 'one-time-code'
  | 'organization-title'
  | 'organization'
  | 'street-address'
  | 'address-line1'
  | 'address-line2'
  | 'address-line3'
  | 'address-level4'
  | 'address-level3'
  | 'address-level2'
  | 'address-level1'
  | 'country'
  | 'country-name'
  | 'postal-code'
  | 'cc-name'
  | 'cc-given-name'
  | 'cc-additional-name'
  | 'cc-family-name'
  | 'cc-number'
  | 'cc-exp'
  | 'cc-exp-month'
  | 'cc-exp-year'
  | 'cc-csc'
  | 'cc-type'
  | 'transaction-currency'
  | 'transaction-amount'
  | 'language'
  | 'bday'
  | 'bday-day'
  | 'bday-month'
  | 'bday-year'
  | 'sex'
  | 'tel'
  | 'tel-extension'
  | 'impp'
  | 'url'
  | 'photo';

interface IProps {
  label: string;
  cleaveOptions: CleaveOptions;
  id?: string;
  className?: string;
  type?: string;
  autoFocus?: boolean;
  onBlur?: any;
  mask?: boolean;
  trim?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  autoComplete?: AutocompleteType;
  helper?: () => {};
}

const TextField: React.FC<FieldProps & IProps> = ({
  id,
  className,
  field,
  form: { errors, touched, setFieldValue, isSubmitting },
  cleaveOptions,
  label,
  type = 'text',
  onBlur,
  autoFocus,
  mask,
  trim = true,
  disabled = false,
  readOnly = false,
  autoComplete = 'on',
  helper,
}) => {
  const [focused, setFocused] = useState(false);
  const [focusCount, setFocusCount] = useState(0);
  const maskedValue = field.value ? field.value.replace(/./g, '*') : '';
  useEffect(() => {
    if (isSubmitting && autoFocus) setFocusCount(focusCount + 1);
  }, [isSubmitting]);

  const validationError = !!errors[field.name] && !!touched[field.name];
  const isTouched = !!touched[field.name];
  const valid = !!errors[field.name];

  return (
    <TextInputWrapper
      className={className}
      validationError={validationError}
      autoFocus={autoFocus}
      focusCount={focusCount}
      disabled={disabled}
    >
      <Cleave
        {...field}
        id={id}
        type={type || 'text'}
        autoComplete={autoComplete}
        value={mask && !focused ? maskedValue : field.value}
        options={cleaveOptions}
        readOnly={readOnly}
        disabled={disabled}
        required
        aria-labelledby={`${field.name}-label`}
        onBlur={(event) => {
          // Trim leading and trailing whitespace
          if (trim) setFieldValue(field.name, event.target.value.trim());
          field.onBlur(event);
          if (onBlur) onBlur();
          setFocused(false);
          setFocusCount(focusCount + 1);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        autoFocus={autoFocus}
        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
          setFocusCount(focusCount + 1);
          if (type === 'tel' && !/^\d*$/.test(e.key)) {
            return e.preventDefault();
          }
        }}
      />
      <Label id={`${field.name}-label`} css={field.value && focusCssStyle}>
        {label}
      </Label>
      {!!helper && (
        <StyledIcon
          color={Colors.gray}
          iconName={IconType.Helper}
          style={{
            right: '2.5rem',
          }}
          onClick={helper}
        />
      )}
      {isTouched ? (
        valid && (autoFocus ? focusCount > 0 : true) ? (
          <StyledIcon
            color={Colors.darkStrawberry}
            iconName={IconType.Error}
            style={{
              right: '1rem',
            }}
          />
        ) : (
          <StyledIcon
            color={Colors.indigo}
            iconName={IconType.Valid}
            style={{
              right: '1rem',
            }}
          />
        )
      ) : null}
      {(autoFocus ? focusCount > 1 : true) && !!errors[field.name] && !!touched[field.name] ? (
        <ValidationError>{errors[field.name]}</ValidationError>
      ) : null}
    </TextInputWrapper>
  );
};

export default TextField;
